import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const motionPolishSkill: Skill = {
    name: SKILL_NAMES.motionPolish,
    kind: 'codeo',
    description:
        'The craft layer that separates amateur motion from produced motion — easing vocabulary, ' +
        'overlap, stagger, anticipation and settle. Load it whenever animation feels flat, robotic, ' +
        'or "everything fades in at once".',
    instructions: `# Motion Polish

Most flat motion isn't missing animation — it has the wrong *craft*. Same elements, same timeline, but everything moves linearly, simultaneously, and ends abruptly. These are the levers, in order of impact, that make motion read as produced. All of it is core GSAP on the single paused timeline (\`window.__timelines.main\`) — no plugins, no CSS animation.

## 1. Easing vocabulary (never \`linear\` for entrances)

Linear motion is the single biggest tell. Match the ease to the intent:

- **Entrances** → \`power3.out\` / \`power4.out\` — fast start, decelerate into place. The element *arrives*.
- **Exits** → \`power2.in\` / \`power3.in\` — accelerate away.
- **Playful pop** → \`back.out(1.5)\` — a small overshoot past the target, then settle. Great for icons, badges, stat numbers.
- **Snappy/confident** → \`expo.out\` — very fast settle, premium and decisive.
- **Ambient / loops** → \`sine.inOut\` — smooth both ends, no acceleration tells.
- **Continuous spin / marquee** → \`none\` is the *only* correct linear use.

## 2. Overlap (the biggest lever)

Amateur timelines sequence beats end-to-end: in, pause, out. Produced timelines *overlap* — the next beat starts while the previous is still finishing (~40–60% in). Use absolute positions or relative offsets, not the default append:

\`\`\`js
// flat: each beat waits for the last
tl.from('#title', { y: 30, opacity: 0, duration: 0.6 });
tl.from('#sub',   { y: 30, opacity: 0, duration: 0.6 });   // starts at 0.6

// polished: subtitle rises while the title is still settling
tl.from('#title', { y: 40, opacity: 0, duration: 0.7, ease: 'power3.out' }, 0.0);
tl.from('#sub',   { y: 28, opacity: 0, duration: 0.6, ease: 'power3.out' }, 0.25);
\`\`\`

## 3. Stagger (reveal groups, never all-at-once)

A row of cards, list items, or logos should cascade, not pop together:

\`\`\`js
tl.from('.feature-card', { y: 40, opacity: 0, duration: 0.55, ease: 'power3.out',
    stagger: 0.09 }, 0.4);
\`\`\`

Stagger 0.06–0.12s reads as a deliberate cascade; larger drags, smaller looks simultaneous. For symmetry, \`stagger: { each: 0.08, from: 'center' }\`.

## 4. Anticipation & settle

Real things wind up before they move and overshoot before they rest:

\`\`\`js
// settle: overshoot, then ease back to the resting value
tl.fromTo('#badge', { scale: 0 },
    { scale: 1.12, duration: 0.35, ease: 'power2.out' }, 1.0);
tl.to('#badge', { scale: 1, duration: 0.25, ease: 'power2.inOut' }, 1.35);
\`\`\`

\`back.out()\` bakes the overshoot into one tween; the explicit two-step gives finer control for hero beats.

## 5. Secondary motion

When a primary element moves, link a secondary one a beat later — a shadow settling, a caption catching up, an underline drawing after the word lands. The lag (0.1–0.25s) is what makes it feel physical rather than rigid.

## Before → after, one beat

\`\`\`js
// FLAT
tl.from('#headline', { opacity: 0, duration: 0.5 });
tl.from('.stat',     { opacity: 0, duration: 0.5 });

// POLISHED
tl.from('#headline', { y: 36, opacity: 0, duration: 0.7, ease: 'power3.out' }, 0.0);
tl.from('.stat',     { y: 24, opacity: 0, duration: 0.55, ease: 'back.out(1.4)',
    stagger: 0.1 }, 0.3);
\`\`\`

## Pitfalls

- Don't over-ease: \`back\`/\`elastic\` on *everything* turns confident motion into a bouncy toy. Reserve overshoot for accent beats; keep body content on \`power\` eases.
- Overlap improves flow but watch readability — text the viewer must read needs to fully land and hold before the next beat steals attention.
- Stagger multiplies onto duration: stagger 0.1 across 8 items adds 0.8s of tail. Account for it so the group finishes inside its window.
- All \`from\` entrances above start from a hidden state (\`opacity: 0\`), so default \`immediateRender\` keeps them hidden until they run — don't reach for \`immediateRender: false\`.
- Polish still lives entirely on the single paused timeline with finite repeats — it never means reaching for CSS \`@keyframes\`/\`transition\` or an infinite repeat, which don't seek.`,
};
